/*
 * bc-variant-recolor — variant-linked color for STATIC diagram surfaces.
 *
 * <bc-diagram> instances handle their own variant color inside
 * assets/bc-diagrams.js; this module covers pure-Liquid renders with no
 * custom element — today the Dimensions cross-section
 * (snippets/bc-webbing-cross-section.liquid), which lives in the doc panel
 * far outside the product form's section scope.
 *
 * Contract: a root marked [data-bc-variant-recolor] carries a
 * script[data-bc-variant-color-map] child ({"option":"Color","colors":
 * {value: hex}} — emitted by snippets/bc-diagram-variant-color-map.liquid)
 * and [data-bc-vc] descendants naming the presentation attribute to
 * rewrite ("fill" / "stroke"). On every variant change the selected value
 * of the map's option is read from the MAIN product form (quick-add-dialog
 * pickers are ignored — a modal selection is a different product context
 * and must not repaint the page's doc panel) and the mapped hex is applied.
 * No selection / no mapping -> the current colors are left alone.
 *
 * Also handles whole-pattern PANE SWAPS: a root marked [data-bc-cs-swap]
 * (snippets/bc-cross-section-variants.liquid) carries one pre-rendered
 * cross-section per [data-bc-cs-value] child; the selected value of
 * [data-bc-cs-option]'s option decides which pane is visible. Used where a
 * recolor can't express the difference (two-tone bodies, pattern marks —
 * the merged Jelly PRO's Classic vs Desert). An unmapped selection leaves
 * the panes untouched (the Liquid render already shows the fallback pane).
 *
 * Listens on document: `change` (radio/dropdown picks, fires before any
 * morph) and `variant:update` (covers picker section morphs that replace
 * the inputs). Loaded ONCE with `defer` by sections/bc-product-docs.liquid
 * (several recolor roots can render in one panel), so the initial DOM is
 * parsed by the time apply() first runs; the Liquid render already painted
 * the selected variant's color server-side, making the initial apply()
 * a visual no-op.
 *
 * Both listeners fire for a single variant pick — `change` immediately, then
 * `variant:update` once the picker's section fetch lands — so every root
 * remembers what it last applied and short-circuits when the answer is
 * unchanged (the same `_lastValue` shape <bc-sewing-figure> uses in
 * assets/bc-segment-diagram.js). That covers the repeat pass, `change`
 * events from other option groups, and any other component's variant
 * dispatch. These roots live in the docs panel, which is a separate section
 * from the product form and therefore outside the morph, so nothing resets
 * the attributes behind our back and the memo can be trusted. The parsed
 * color map is memoized on the same pass — it used to be JSON.parse'd per
 * root per event.
 */
(() => {
  if (window.bcVariantRecolor) return;
  window.bcVariantRecolor = true;

  /**
   * Selected value of the named variant option in the main product form.
   * Delegates to the shared reader (assets/bc-variant-resolve.js); the
   * exclude keeps quick-add-modal copies of the picker from shadowing the
   * page-level one.
   */
  const findSelected = (optionName) =>
    window.bcVariantResolve?.readSelectedOption(document, optionName, { exclude: 'quick-add-dialog' }) || null;

  // Per-root memos: the parsed color map (`false` = unparseable, don't retry)
  // and the last value actually applied. Weak so a re-rendered section's
  // roots are collected with the DOM.
  const maps = new WeakMap();
  const appliedHex = new WeakMap();
  const appliedPane = new WeakMap();

  const colorMap = (root) => {
    if (maps.has(root)) return maps.get(root);
    let map = false;
    try {
      const parsed = JSON.parse(
        root.querySelector('script[data-bc-variant-color-map]')?.textContent || ''
      );
      if (parsed && typeof parsed.colors === 'object') map = parsed;
    } catch (_e) {
      map = false;
    }
    maps.set(root, map);
    return map;
  };

  const apply = () => {
    // One read per option name per pass, shared across roots — several
    // cross-sections in one panel normally watch the same option.
    const selected = {};
    const read = (optionName) => {
      if (!(optionName in selected)) selected[optionName] = findSelected(optionName);
      return selected[optionName];
    };

    for (const root of document.querySelectorAll('[data-bc-variant-recolor]')) {
      const map = colorMap(root);
      if (!map) continue;
      const value = read(map.option || 'Color');
      const hex = value && map.colors[value];
      if (typeof hex !== 'string' || !hex) continue;
      if (appliedHex.get(root) === hex) continue;
      appliedHex.set(root, hex);
      for (const el of root.querySelectorAll('[data-bc-vc]')) {
        const attr = el.dataset.bcVc === 'stroke' ? 'stroke' : 'fill';
        el.setAttribute(attr, hex);
      }
    }
    for (const root of document.querySelectorAll('[data-bc-cs-swap]')) {
      const value = read(root.dataset.bcCsOption || 'Color');
      if (!value) continue;
      if (appliedPane.get(root) === value) continue;
      const panes = root.querySelectorAll('[data-bc-cs-value]');
      // An unmapped value leaves the panes alone (the Liquid render already
      // shows the fallback), and is NOT memoized — nothing was applied.
      if (![...panes].some((p) => p.dataset.bcCsValue === value)) continue;
      appliedPane.set(root, value);
      for (const p of panes) p.hidden = p.dataset.bcCsValue !== value;
    }
  };

  document.addEventListener('change', (e) => {
    if (e.target?.closest?.('fieldset.variant-option, .variant-option--dropdowns')) apply();
  });
  document.addEventListener('variant:update', apply);
  apply();
})();
